import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

const backendApi = config.backendApi;

export interface PolicyApprover {
  approverType: 'user' | 'team';
  approverId: string;
}

export interface PolicyStage {
  stageIndex: number;
  name: string;
  quorumKind: 'all' | 'any' | 'n_of';
  quorumN: number | null;
  approvers: PolicyApprover[];
}

export interface ApprovalPolicy {
  id: string;
  organizationId: string;
  divisionId: string | null;
  contextId: string | null;
  name: string;
  enabled: boolean;
  priority: number;
  changeTypes: string[];
  riskLevels: string[];
  deviceTags: string[];
  deviceRoles: string[];
  minDeviceCount: number | null;
  autoApprove: boolean;
  stages: PolicyStage[];
}

interface ApprovalPoliciesResponse {
  policies: ApprovalPolicy[];
}

export interface StageInput {
  name: string;
  quorumKind: 'all' | 'any' | 'n_of';
  quorumN?: number;
  approvers: PolicyApprover[];
}

export interface CreateApprovalPolicyRequest {
  name: string;
  divisionId?: string | null;
  contextId?: string | null;
  enabled?: boolean;
  priority?: number;
  changeTypes?: string[];
  riskLevels?: string[];
  deviceTags?: string[];
  minDeviceCount?: number;
  autoApprove?: boolean;
  stages: StageInput[];
}

export function useApprovalPolicies() {
  return useQuery<ApprovalPolicy[]>({
    queryKey: ['approval-policies'],
    queryFn: async () => {
      const response = await axios.get<ApprovalPoliciesResponse>(`${backendApi}/api/approval-policies`, {
        withCredentials: true,
      });
      return response.data.policies;
    },
  });
}

export function useCreateApprovalPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateApprovalPolicyRequest) => {
      const response = await axios.post<ApprovalPolicy>(`${backendApi}/api/approval-policies`, data, {
        withCredentials: true,
      });
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-policies'] });
    },
  });
}

export function useDeleteApprovalPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.delete(`${backendApi}/api/approval-policies/${id}`, { withCredentials: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['approval-policies'] });
    },
  });
}
